import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Badge, Button, Spinner } from 'react-bootstrap';
import styles from './NoticeComponent.module.css';
import api from '../../seller-apis/api'; 
import ImageCarousel from './ImageCarousel';
import { Actions } from '../../Actions/Actions';
import { useDispatch } from 'react-redux';

// Import Open Sans font
const fontImportStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap');
`;

const Notices = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [acknowledgedNotices, setAcknowledgedNotices] = useState({});
  const [acknowledging, setAcknowledging] = useState({});
  const currentVendorId = localStorage.getItem('vendorId');
   const dispatch = useDispatch();
  
  // Define inline styles for the acknowledgment button and status
  const inlineStyles = {
    acknowledgeButton: {
      backgroundColor: '#165b5b',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '5px 12px',
      fontSize: '13px',
      fontWeight: '500',
      transition: 'background-color 0.2s',
      marginLeft: '10px'
    },
    acknowledged: {
      color: '#165b5b',
      fontSize: '13px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      marginLeft: '10px'
    },
    publishDate: {
      color: '#666',
      fontSize: '12px',
      fontWeight: '400',
      marginTop: '4px'
    }
  };

  useEffect(() => {
    // Add Open Sans font to document head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = fontImportStyle;
    document.head.appendChild(styleElement);

    // Clean up function
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    // Fetch notices when component mounts
    fetchNotices();
  }, []);

  // Function to fetch notices from API
  const fetchNotices = async () => {
    try {
      setLoading(true);
      
      // Using updated API call with POST method
      const response = await api.notices.getActiveNotices(
        currentVendorId,
        "1",  // pageNo
        null  
      );
      
      if (response && response.data && response.data.data && response.data.data.length > 0) {
        // Filter notices by type = 'Notice Page'
        const filteredNotices = response.data.data.filter(notice => notice.type === 'Notice Page');
        setNotices(filteredNotices);
        Actions.updatePendingNoticeCount(dispatch,response?.data?.stats?.pending)
        
        // Initialize acknowledgement status with the fetched data
        updateAcknowledgementStatus(filteredNotices);
      } else {
        setNotices([]);
      }
    } catch (err) {
      console.error('Error fetching notices:', err);
      setError("Error fetching notices. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  // Update acknowledgement status based on fetched notices
  const updateAcknowledgementStatus = (noticesList) => {
    const newAcknowledgedMap = {};
    
    noticesList.forEach(notice => {
      // Check if this notice has been acknowledged by the current vendor
      const isAcknowledgedByCurrentVendor = notice.acknowledgeUserIds?.some(
        ack => ack.vendorId === currentVendorId
      );
      
      // Save the status in our map
      newAcknowledgedMap[notice._id] = !!isAcknowledgedByCurrentVendor;
    });
    
    // Update state with new acknowledgement map
    setAcknowledgedNotices(newAcknowledgedMap);
  };
  
  // Function to handle acknowledging a notice with fixed error handling
  const acknowledgeNotice = async (noticeId) => {
    try {
      // Set acknowledging state
      setAcknowledging(prev => ({ ...prev, [noticeId]: true }));
      
      const payload = { 
        _id: noticeId,
        vendorId: currentVendorId
      };
      
      // Make the acknowledgement API call
      await api.notices.acknowledgeNotice(payload);
      
      // Update local state
      setAcknowledgedNotices(prev => ({
        ...prev,
        [noticeId]: true
      }));

     
      
      // IMPORTANT: Fetch fresh data to update stats
      fetchNotices();
      
    } catch (error) {
      console.error('Error acknowledging notice:', error);
      
      // If error is "already acknowledged"
      if (error.response?.data?.message?.includes('already acknowledged')) {
        // Update UI state to show as acknowledged
        setAcknowledgedNotices(prev => ({
          ...prev,
          [noticeId]: true
        }));
        
        // Also refresh data even on "already acknowledged" error
        fetchNotices();
      } else {
        alert('Failed to acknowledge notice. Please try again.');
      }
    } finally {
      setAcknowledging(prev => ({ ...prev, [noticeId]: false }));
    }
  };

  // Apply Quill styles programmatically after render
  useEffect(() => {
    // Apply styles directly to Quill elements for maximum compatibility
    const applyQuillStyles = () => {
      // Large text size
      document.querySelectorAll('.ql-size-large').forEach(el => {
        el.style.setProperty('font-size', '18px', 'important');
      });
      
      // Extra small text size
      document.querySelectorAll('.ql-size-xtrasmall').forEach(el => {
        el.style.setProperty('font-size', '10px', 'important');
      });
      
      // Small text size
      document.querySelectorAll('.ql-size-small').forEach(el => {
        el.style.setProperty('font-size', '12px', 'important');
      });
      
      // Medium text size
      document.querySelectorAll('.ql-size-medium').forEach(el => {
        el.style.setProperty('font-size', '14px', 'important');
      });
      
      // Extra large text size
      document.querySelectorAll('.ql-size-xtralarge').forEach(el => {
        el.style.setProperty('font-size', '24px', 'important');
      });
      
      // Huge text size
      document.querySelectorAll('.ql-size-huge').forEach(el => {
        el.style.setProperty('font-size', '32px', 'important');
      });
      
      // Very huge text size
      document.querySelectorAll('.ql-size-veryhuge').forEach(el => {
        el.style.setProperty('font-size', '40px', 'important');
      });
    };
    
    // Apply once DOM is loaded and then whenever notices change
    applyQuillStyles();
    
    // Set up a MutationObserver to watch for dynamic content changes
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          applyQuillStyles();
        }
      });
    });
    
    // Start observing the document body for changes
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    // Clean up
    return () => {
      observer.disconnect();
    };
  }, [notices]);

  const toggleDescription = (noticeId, e) => {
    e.preventDefault();
    setExpandedDescriptions(prev => ({
      ...prev,
      [noticeId]: !prev[noticeId]
    }));
  };

  const isValidImageUrl = (url) => {
    if (!url) return false;
    
    // Check if it's a URL string first (not an object)
    if (typeof url !== 'string') return false;
    
    // Check if it's a URL and appears to be an image
    const isLikelyImageUrl = url.match(/\.(jpeg|jpg|gif|png|webp|bmp|svg)$/) !== null || 
                            url.includes('http') && !url.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|zip|rar)$/);
    
    return isLikelyImageUrl;
  };

  // Format date to a more readable format
  const formatPublishDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) return '';
    
    // Format: April 18, 2025
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderDescription = (notice) => {
    // Decode HTML entities to actual HTML
    const decodeHTML = (html) => {
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    };
  
    // Decode the message
    const decodedMessage = decodeHTML(notice.message);
  
    // Extract plain text from the message (for length calculation)
    const extractPlainText = (html) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || '';
    };
  
    const plainTextMessage = extractPlainText(decodedMessage);
    const isExpanded = expandedDescriptions[notice._id];
    const maxLength = 1000;
    
    // Determine if the message is long enough to truncate
    const isLongText = plainTextMessage.length > maxLength;
    
    // If it's short enough, just render the HTML
    if (!isLongText) {
      return (
        <div 
          className={styles.noticeItemDescription}
          dangerouslySetInnerHTML={{ __html: decodedMessage }}
        />
      );
    }
    
    // For longer text that needs truncation
    return (
      <>
        <div 
          className={styles.noticeItemDescription}
          dangerouslySetInnerHTML={{ 
            __html: isExpanded 
              ? decodedMessage 
              : decodedMessage.substring(0, plainTextMessage.indexOf(plainTextMessage.substring(maxLength))) + '...'
          }}
        />
        <Button 
          variant="link"
          className={styles.readMoreButton}
          onClick={(e) => toggleDescription(notice._id, e)}
          style={{ 
            padding: 0, 
            color: '#165b5b', 
            textDecoration: 'none', 
            fontSize: '13px',
            fontWeight: '500'
          }}
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </Button>
      </>
    );
  };
  
  // Function to get images from notice data
  const getNoticeImages = (notice) => {
    let images = [];
    
    // Check if there are images in the image array
    if (notice.image && notice.image.length > 0) {
      images = images.concat(notice.image.filter(url => url && url.trim() !== ''));
    }
    
    // Check if there are images in the url array (handle both formats)
    if (notice.url && notice.url.length > 0) {
      // Handle cases where url is an array of strings
      if (typeof notice.url[0] === 'string') {
        images = images.concat(notice.url.filter(url => url && url.trim() !== ''));
      } 
      // Handle cases where url is an array of objects with url property
      else if (typeof notice.url[0] === 'object') {
        const urlImages = notice.url
          .filter(item => item && typeof item === 'object' && item.url && item.url.trim() !== '')
          .map(item => item.url)
          .filter(url => isValidImageUrl(url));
        
        images = images.concat(urlImages);
      }
    }
    
    // Check if there are images in attachments
    if (notice.attachments && notice.attachments.length > 0) {
      let attachmentImages = [];
      
      if (Array.isArray(notice.attachments[0])) {
        // Handle nested array case
        notice.attachments.forEach(attachmentGroup => {
          if (Array.isArray(attachmentGroup)) {
            const imageAttachments = attachmentGroup
              .filter(attachment => attachment && isValidImageUrl(attachment));
            attachmentImages = attachmentImages.concat(imageAttachments);
          }
        });
      } else {
        // Handle flat array case
        const imageAttachments = notice.attachments
          .filter(attachment => attachment && isValidImageUrl(attachment));
        attachmentImages = attachmentImages.concat(imageAttachments);
      }
      
      images = images.concat(attachmentImages);
    }
    
    // Return unique images
    return [...new Set(images)].filter(img => img && isValidImageUrl(img));
  };

  // Loading state
  if (loading) {
    return (
      <Container fluid className={styles.pageContainer}>
        <div className={styles.noticeHeader}>
          <h1 className={styles.noticeTitle}>Notices</h1>
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
        <div className="text-center p-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading notices...</p>
        </div>
      </Container>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Container fluid className={styles.pageContainer}>
        <div className={styles.noticeHeader}>
          <h1 className={styles.noticeTitle}>Notices</h1>
          <Badge bg="danger">Error</Badge>
        </div>
        <div className="alert alert-danger mt-3">{error}</div>
      </Container>
    );
  }

  return (
    <Container fluid className={styles.pageContainer}>
      {/* Header section with Notices count */}
      <div className={styles.noticeHeader}>
        <h1 className={styles.noticeTitle}>Notices</h1>
        <Badge 
          pill 
          bg="dark" 
          style={{ 
            backgroundColor: '#165b5b',
            fontSize: '14px',
            padding: '4px 12px'
          }}
        >
          {notices.length} Notices
        </Badge>
      </div>
      
      {/* Main content */}
      <div className={styles.mainContent}>
        {/* Notice List */}
        <div className={styles.noticeList}>
          {notices.length === 0 ? (
            <div className="text-center p-4 bg-white rounded shadow-sm">
              No notices available
            </div>
          ) : (
            notices.map((notice) => (
              <div key={notice._id} className="bg-white rounded shadow-sm mb-3 overflow-hidden">
                <div className="p-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="m-0 fs-5 fw-semibold">{notice.title}</h3>
                    {/* Add publish date below title */}
                    <div style={inlineStyles.publishDate}>
                      Published: {formatPublishDate(notice.publishDate)}
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    {/* Acknowledgment button if required */}
                    {notice.acknowledgement === 'Yes' && (
                      acknowledgedNotices[notice._id] ? (
                        <span className="text-success" style={inlineStyles.acknowledged}>
                          Acknowledged
                        </span>
                      ) : (
                        <Button 
                          size="sm"
                          style={inlineStyles.acknowledgeButton}
                          onClick={(e) => {
                            e.preventDefault();
                            acknowledgeNotice(notice._id);
                          }}
                          disabled={acknowledging[notice._id]}
                        >
                          {acknowledging[notice._id] ? 'Processing...' : 'Acknowledge'}
                        </Button>
                      )
                    )}
                  </div>
                </div>
                
                <div className="px-3 pb-3">
                  <div>
                    {renderDescription(notice)}
                    
                    {/* Attachment Links */}
                    {notice.attachments && notice.attachments.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {Array.isArray(notice.attachments[0]) 
                          ? notice.attachments[0].map((attachment, index) => (
                              <a 
                                key={index}
                                href={attachment} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="badge bg-light text-primary text-decoration-none"
                                style={{ fontSize: '13px', padding: '4px 10px' }}
                              >
                                PDF Attachment link
                              </a>
                            ))
                          : notice.attachments.map((attachment, index) => (
                              <a 
                                key={index}
                                href={attachment} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="badge bg-light text-primary text-decoration-none"
                                style={{ fontSize: '13px', padding: '4px 10px' }}
                              >
                                PDF Attachment link
                              </a>
                            ))
                        }
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Image Carousel - Display all images with automatic sliding */}
                {getNoticeImages(notice).length > 0 && (
                  <ImageCarousel 
                    images={getNoticeImages(notice)}
                    title={notice.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://www.toolsvilla.in/admin/assets/img/Toolsvilla_logo.png';
                      e.target.className = `${styles.noticeItemImage} ${styles.fallbackImage}`;
                    }}
                  />
                )}
                
                <div className={`${styles.noticeItemFooter} p-2 bg-light border-top d-flex justify-content-between`}></div>
              </div>
            ))
          )}
        </div>
      </div>
    </Container>
  );
};

export default Notices;